import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ADVANCE_MOVEMENT_LABELS,
  readAdvanceMovement,
  type SaldoProposal,
  type BankMovementResponse,
  canWriteOff,
  cardMonthLabel,
  formatMoney,
  reconciliationCheck,
  fromCents,
  type InvoiceListItem,
  type InvoiceKind,
  type InvoiceOrigin,
  type InvoicesResponse,
  LOAN_MOVEMENT_KIND_LABELS,
  type LoanSummary,
  parseDecimal,
  payableInvoices,
  RECONCILIATION_REASON_LABELS,
  RECONCILIATION_REASONS,
  SALARY_PAYMENT_KIND_LABELS,
  salaryPeriodLabel,
  type MovementInvoicesRequest,
  type MovementRestRequest,
  type ReconcileRequest,
  type ReconciliationProposal,
  type ReconciliationReason,
  suggestedReason,
  toCents,
  isTransferReason,
  type TransferChoice,
} from '@k-management/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { FloatingPanel } from '../../components/FloatingPanel.js';
import { Icon } from '../../components/Icon.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { moneyOf } from '../people/people-ui.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { type SalaryLine, SalaryLines, salaryLinesCents } from './SalaryLines.js';
import { InstalmentDuesPicker } from './InstalmentDuesPicker.js';
import { NoInvoiceActions } from './NoInvoicePayment.js';
import { SubjectPaymentDialog } from './SubjectPayments.js';
import { type TfrLine, TfrLines, tfrLinesCents } from './TfrLines.js';
import { TransferPicker } from './TransferPicker.js';
import { ReversalPicker } from './ReversalPicker.js';
import {
  announceTreasuryChange,
  CreditNoteMark,
  movementPath,
  treasuryKeys,
} from './treasury-ui.js';

interface Candidate {
  invoiceId: string;
  number: string;
  date: string;
  subjectId: string | null;
  counterpartyName: string;
  residual: string;
  reasons: string[];
  best: boolean;
  /** «apertura»: an item of the situation at 1 January (1.48). */
  origin: InvoiceOrigin;
  /** To mark the credit notes (2.12). */
  kind?: InvoiceKind;
}

/** How many other invoices the list shows before asking for narrower filters. */
const LIST_LIMIT = 15;

/** The cents of an amount typed in a field, zero when empty or not a number. */
function centsOf(value: string): number {
  return Math.round((parseDecimal(value) ?? 0) * 100);
}

/** The title of a movement, for the panel and the separate window. */
export function movementTitle(movement: BankMovementResponse): string {
  return `${Number(movement.amount) > 0 ? 'Entrata' : 'Uscita'} del ${formatDate(movement.bookingDate)} · ${formatMoney(movement.amount)}`;
}

/** The address of the separate window that reconciles a movement. */
export function reconcileWindowPath(code: string, movementId: string): string {
  return `/tesoreria/banche/${code}/movimenti/${movementId}/riconcilia`;
}

/** The movement reconciled by the panel in the page: it does not block the page under it. */
export function ReconcilePanel({
  code,
  movement,
  onClose,
  onDetach,
}: {
  code: string;
  movement: BankMovementResponse;
  onClose: () => void;
  onDetach: () => void;
}) {
  return (
    <FloatingPanel
      title={movementTitle(movement)}
      onClose={onClose}
      extra={
        <button
          type="button"
          className="small"
          title="Apri in una finestra separata del browser"
          onClick={onDetach}
        >
          Sgancia
        </button>
      }
    >
      <ReconcileForm key={movement.id} code={code} movement={movement} onDone={onClose} />
    </FloatingPanel>
  );
}

/**
 * Reconciling a bank movement (07/10/2026): the invoices the rules propose,
 * best first, the other open invoices by date, counterparty and search, the
 * part of the movement for each; or a bank cost, or a reason. A reconciled
 * movement shows what it is reconciled with and can be undone.
 */
export function ReconcileForm({
  code,
  movement,
  onDone,
}: {
  code: string;
  movement: BankMovementResponse;
  /** After a change; without it the form stays, showing the new state. */
  onDone?: () => void;
}) {
  const queryClient = useQueryClient();
  const canWrite = useCan('tesoreria.write');
  // Salaries are matched to the employees only with the module Persone (1.35).
  const canPeople = useCan('persone.write');
  const canCards = useCan('tesoreria.cards');
  const [salaryLines, setSalaryLines] = useState<SalaryLine[]>([]);
  const [tfrLines, setTfrLines] = useState<TfrLine[]>([]);
  // Invoices, salaries or TFR for less than the movement: confirmed on purpose (1.69, 1.76).
  const [short, setShort] = useState<{ body: ReconcileRequest; left: number } | null>(null);
  const base = `/treasury/banks/${code}/movements/${movement.id}`;
  const incoming = Number(movement.amount) > 0;
  // Invoices that do not make the whole movement: more can be added on the part left (1.76).
  const check = movement.reconciliation
    ? reconciliationCheck(movement.amount, movement.reconciliation)
    : null;
  const freeCents =
    movement.reconciliation?.kind === 'fatture' && check ? toCents(check.unassigned) : 0;
  const [adding, setAdding] = useState(false);
  // Recognised as bank costs by its causale but paying an invoice (a fee invoiced, 2.26): the form opens.
  const [notCosts, setNotCosts] = useState(false);
  const reconciled = adding || notCosts ? null : movement.reconciliation;
  const proposals = useQuery({
    queryKey: ['treasury', 'banks', code, 'proposals', movement.id, adding],
    queryFn: () => apiRequest<ReconciliationProposal[]>(`${base}/proposals`),
    enabled: !reconciled,
  });
  const invoices = useQuery({
    queryKey: treasuryKeys.invoices,
    queryFn: () => apiRequest<InvoicesResponse>('/treasury/invoices'),
    enabled: !reconciled && canWrite,
  });
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  // Abbuono (1.81): the invoices whose small residual the user does not want closed.
  const [keepOpen, setKeepOpen] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  // From the start of the year before to 90 days after the movement: an
  // invoice may follow the payment of its pro forma (1.41).
  const [from, setFrom] = useState(`${Number(movement.bookingDate.slice(0, 4)) - 1}-01-01`);
  const [to, setTo] = useState(() => {
    const date = new Date(`${movement.bookingDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + 90);
    return date.toISOString().slice(0, 10);
  });
  const [onlyCounterparty, setOnlyCounterparty] = useState(true);
  const [reason, setReason] = useState<ReconciliationReason | null>(null);
  const [note, setNote] = useState('');
  // The other side of a transfer or a withdrawal (3.13).
  const [transfer, setTransfer] = useState<TransferChoice | null>(null);
  // The instalments of the rateazioni it pays (3.15), and what else, written by hand.
  const [dues, setDues] = useState<{ ids: string[]; cents: number }>({ ids: [], cents: 0 });
  const onDues = useCallback((ids: string[], cents: number) => setDues({ ids, cents }), []);
  const [duesRest, setDuesRest] = useState('');
  // The loan instalment a reversal of the bank takes back (3.16).
  const [reversedId, setReversedId] = useState<string | null>(null);
  // A movement of a loan matched by hand (1.50).
  const [loanId, setLoanId] = useState('');
  const loans = useQuery({
    queryKey: ['treasury', 'loans'],
    queryFn: () => apiRequest<LoanSummary[]>('/treasury/loans'),
    enabled: reason === 'finanziamento',
  });
  const suggested = suggestedReason(movement);
  // Intesa (1.63): a customer's payment with the part kept by the bank for its advance.
  const advanceReading = readAdvanceMovement(movement);
  const saldo = useQuery({
    queryKey: ['treasury', 'advances', 'saldo', movement.id],
    queryFn: () => apiRequest<SaldoProposal | null>(`/treasury/advances/saldo/${movement.id}`),
    enabled: !reconciled && !adding && advanceReading?.kind === 'saldo',
  });
  const saldoSorting = useTableSort('reconcile.saldo', saldo.data?.invoices ?? [], {
    invoice: (item) => item.number,
    fromMovement: (item) => amountSort(item.fromMovement),
    retained: (item) => amountSort(item.retained),
    residual: (item) => amountSort(item.residual),
  });

  // The proposals arrive: they are the first candidates, the best one chosen.
  useEffect(() => {
    if (!proposals.data) return;
    setCandidates(proposals.data.map(({ amount: _amount, score: _score, ...item }) => item));
    // The best: one invoice, or several that make the movement together (1.52).
    const best = proposals.data.filter((item) => item.best);
    if (best.length)
      setChosen(Object.fromEntries(best.map((item) => [item.invoiceId, item.amount])));
    // A salary without invoices to propose: the reason is ready to confirm.
    else if (suggested && proposals.data.length === 0) setReason(suggested);
  }, [proposals.data, suggested]);

  // The counterparty the rules recognised in the description, to narrow the list to it.
  const recognised = proposals.data?.find((item) =>
    item.reasons.includes('nome della controparte'),
  );
  const sameCounterparty = (item: Pick<InvoiceListItem, 'subjectId' | 'counterpartyName'>) =>
    !recognised ||
    (recognised.subjectId && item.subjectId
      ? recognised.subjectId === item.subjectId
      : recognised.counterpartyName === item.counterpartyName);
  // The items of the situation at 1 January are old by nature: no date filter for them (1.48).
  const inPeriod = (date: string, origin: InvoiceOrigin = 'gestify') =>
    origin === 'apertura' || ((!from || date >= from) && (!to || date <= to));

  // Saved: the panel goes at once, the lists reload behind it.
  const done = () => {
    announceTreasuryChange();
    void queryClient.invalidateQueries({ queryKey: ['treasury'] });
    onDone?.();
  };
  const save = useMutation({
    mutationFn: (body: ReconcileRequest) =>
      adding && body.kind === 'fatture'
        ? // More invoices on the part left (1.76): the others stay.
          apiRequest(`${base}/invoices`, {
            method: 'POST',
            body: { allocations: body.allocations } satisfies MovementInvoicesRequest,
          })
        : apiRequest(`${base}/reconciliation`, { method: 'PUT', body }),
    onSuccess: () => {
      setAdding(false);
      setNotCosts(false);
      done();
    },
  });
  const undo = useMutation({
    mutationFn: () => apiRequest(`${base}/reconciliation`, { method: 'DELETE' }),
    onSuccess: done,
  });
  // The part not an invoice, written by hand (1.84).
  const [resting, setResting] = useState<{ amount: string; note: string } | null>(null);
  const [shortNote, setShortNote] = useState('');
  const rest = useMutation({
    mutationFn: (body: MovementRestRequest | null) =>
      body
        ? apiRequest(`${base}/rest`, { method: 'PUT', body })
        : apiRequest(`${base}/rest`, { method: 'DELETE' }),
    onSuccess: () => {
      setResting(null);
      setAdding(false);
      done();
    },
  });

  const openRest = () => setResting({ amount: fromCents(freeCents), note: '' });
  // To a subject without a document yet (2.22): the movement, or the part left.
  const [giving, setGiving] = useState(false);
  const removeGiven = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/treasury/subject-payments/${id}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const movementCents = adding ? freeCents : Math.abs(toCents(movement.amount));
  const allocated = Object.values(chosen).reduce((sum, value) => sum + centsOf(value), 0);
  const salaryLinked = reason === 'stipendio' && canPeople && !incoming && salaryLines.length > 0;
  const salaryCents = salaryLinesCents(salaryLines);
  const tfrLinked = reason === 'tfr' && canPeople && !incoming && tfrLines.length > 0;
  const tfrCents = tfrLinesCents(tfrLines);
  const confirmShort = (body: ReconcileRequest, cents: number) =>
    cents < movementCents ? setShort({ body, left: movementCents - cents }) : save.mutate(body);
  const picked = candidates.filter((candidate) => chosen[candidate.invoiceId] !== undefined);
  const pickedResidual = picked.reduce(
    (sum, candidate) => sum + Math.abs(toCents(candidate.residual)),
    0,
  );
  const overpaid = picked.filter(
    (candidate) => centsOf(chosen[candidate.invoiceId]!) > Math.abs(toCents(candidate.residual)),
  );
  // What the movement leaves on an invoice, up to the limit: closed with an abbuono (1.81).
  const leftOf = (candidate: Candidate) =>
    Math.abs(toCents(candidate.residual)) - centsOf(chosen[candidate.invoiceId]!);
  const smallLeft = picked.filter(
    (candidate) => leftOf(candidate) > 0 && canWriteOff(fromCents(leftOf(candidate))),
  );
  const writeOffs = smallLeft
    .filter((candidate) => !keepOpen.includes(candidate.invoiceId))
    .map((candidate) => candidate.invoiceId);
  // The filters hold for the proposals too (1.40); what is chosen always stays.
  const shown = candidates.filter(
    (candidate) =>
      chosen[candidate.invoiceId] !== undefined ||
      (inPeriod(candidate.date, candidate.origin) &&
        (!onlyCounterparty || sameCounterparty(candidate))),
  );
  const needle = search.trim().toLowerCase();
  // A search looks in every open invoice, the proposals hidden by the filters
  // too, regardless of dates and counterparty (1.41).
  // The items of the situation at 1 January only for a movement from that day on (1.49).
  const others = payableInvoices(
    invoices.data?.invoices ?? [],
    movement.bookingDate,
    invoices.data?.openingDate ?? null,
  )
    .filter(
      (item) =>
        item.direction === (incoming ? 'emessa' : 'ricevuta') &&
        item.state !== 'saldata' &&
        !(item.selfBilling && item.direction === 'emessa') &&
        !shown.some((candidate) => candidate.invoiceId === item.id) &&
        (needle ||
          (!candidates.some((candidate) => candidate.invoiceId === item.id) &&
            inPeriod(item.date, item.origin) &&
            (!onlyCounterparty || sameCounterparty(item)))) &&
        (!needle ||
          `${item.number} ${item.counterpartyName} ${formatMoney(item.residual)} ${item.residual}`
            .toLowerCase()
            .includes(needle)),
    )
    .sort((a, b) => b.date.localeCompare(a.date));
  const toggle = (candidate: Candidate) => {
    const next = { ...chosen };
    if (next[candidate.invoiceId] !== undefined) delete next[candidate.invoiceId];
    else {
      const left = Math.max(0, movementCents - allocated);
      next[candidate.invoiceId] = fromCents(
        Math.min(Math.abs(toCents(candidate.residual)), left || movementCents),
      );
    }
    setChosen(next);
  };

  return (
    <div className="stack reconcile-form">
      <p className="muted small movement-full">
        {movement.counterparty && <strong className="block">{movement.counterparty}</strong>}
        {movement.description}
      </p>

      {reconciled ? (
        <>
          <div className="notice info">
            {reconciled.kind === 'fatture' && (
              <>
                Riconciliato con:
                <ul className="plain-list">
                  {reconciled.invoices.map((item) => (
                    <li key={item.id}>
                      <CreditNoteMark kind={item.kind} />
                      <Link to={`/tesoreria/fatture/${item.id}`}>
                        {item.number} · {item.counterpartyName}
                      </Link>{' '}
                      — {formatMoney(item.amount)}
                    </li>
                  ))}
                  {(reconciled.subjectPayments ?? []).map((item) => (
                    <li key={item.id}>
                      {item.withoutInvoice ? 'Senza fattura (costo) a' : 'Senza documento a'}{' '}
                      <Link to={`/tesoreria/schede-contabili/${item.subjectId}`}>
                        {item.subjectName}
                      </Link>{' '}
                      — {formatMoney(item.amount)}
                      {!item.withoutInvoice && toCents(item.available) !== toCents(item.amount) && (
                        <span className="muted"> (disponibili {formatMoney(item.available)})</span>
                      )}
                      {item.note && <span className="muted"> · «{item.note}»</span>}
                      <NoInvoiceActions payment={item} canWrite={canWrite} onDone={done} />
                      {canWrite &&
                        (item.withoutInvoice ||
                          toCents(item.available) === toCents(item.amount)) && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="link"
                              disabled={removeGiven.isPending}
                              onClick={() => removeGiven.mutate(item.id)}
                            >
                              Togli
                            </button>
                          </>
                        )}
                    </li>
                  ))}
                  {reconciled.rest && (
                    <li>
                      Non è una fattura: «{reconciled.rest.note}» —{' '}
                      {formatMoney(reconciled.rest.amount)}
                      {canWrite && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="link"
                            disabled={rest.isPending}
                            onClick={() => rest.mutate(null)}
                          >
                            Togli il resto
                          </button>
                        </>
                      )}
                    </li>
                  )}
                </ul>
              </>
            )}
            {reconciled.kind === 'spese' &&
              (reconciled.auto
                ? 'Spese bancarie, riconosciute dalla causale. Se paga una fattura (un canone fatturato dalla banca), «Non sono spese bancarie» apre la riconciliazione.'
                : 'Riconciliato come spese bancarie.')}
            {reconciled.kind === 'stipendi' &&
              (reconciled.salaries ? (
                <>
                  Stipendi:
                  <ul className="plain-list">
                    {reconciled.salaries.map((item) => (
                      <li key={`${item.employeeId}-${item.year}-${item.month}-${item.kind}`}>
                        <Link to={`/organizzazione/persone/${item.employeeId}`}>{item.name}</Link> ·{' '}
                        {salaryPeriodLabel(item.year, item.month)} ·{' '}
                        {SALARY_PAYMENT_KIND_LABELS[item.kind].toLowerCase()} —{' '}
                        {formatMoney(item.amount)}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                'Stipendio.'
              ))}
            {reconciled.kind === 'tfr' &&
              (reconciled.tfr ? (
                <>
                  TFR versato ai fondi pensione:
                  <ul className="plain-list">
                    {reconciled.tfr.map((item) => (
                      <li key={item.employeeId}>
                        <Link to={`/organizzazione/persone/${item.employeeId}?sezione=tfr`}>
                          {item.name}
                        </Link>{' '}
                        — {formatMoney(item.amount)} ·{' '}
                        {item.coveredFrom && item.coveredTo
                          ? `copre dal ${formatDate(item.coveredFrom)} al ${formatDate(item.coveredTo)}`
                          : 'periodo da indicare'}
                      </li>
                    ))}
                  </ul>
                  {reconciled.note && <small className="muted block">{reconciled.note}</small>}
                </>
              ) : (
                'TFR versato ai fondi pensione.'
              ))}
            {(() => {
              return (
                check && (
                  <div className="warn small">
                    {toCents(check.unassigned) !== 0 && (
                      <p>
                        Assegnati {formatMoney(check.assigned)} su{' '}
                        {formatMoney(fromCents(movementCents))}:{' '}
                        {toCents(check.unassigned) > 0 ? 'restano' : 'superano di'}{' '}
                        {formatMoney(fromCents(Math.abs(toCents(check.unassigned))))}
                        {reconciled.kind === 'fatture' && toCents(check.unassigned) > 0
                          ? ' del movimento non assegnati a nessuna fattura.'
                          : '. Se è un errore, annulla la riconciliazione e rifalla.'}
                      </p>
                    )}
                    {check.overpaid.map((label) => (
                      <p key={label}>{label}: pagato più del netto in busta.</p>
                    ))}
                  </div>
                )
              );
            })()}
            {reconciled.kind === 'motivo' && reconciled.reason && (
              <>
                Non è una fattura: {RECONCILIATION_REASON_LABELS[reconciled.reason]}
                {reconciled.note && ` — ${reconciled.note}`}
                {reconciled.reversal && (
                  <small className="block">
                    Riprende la{' '}
                    <Link
                      to={movementPath(
                        reconciled.reversal.accountCode,
                        reconciled.reversal.movementId,
                      )}
                    >
                      rata del {formatDate(reconciled.reversal.bookingDate)}
                    </Link>
                    {reconciled.reversal.loanName && ` di ${reconciled.reversal.loanName}`}: rata e
                    storno non contano per il mutuo.
                  </small>
                )}
                {isTransferReason(reconciled.reason) && (
                  <small className="block">
                    {reconciled.transfer?.movementId && reconciled.transfer.accountCode ? (
                      <>
                        Con{' '}
                        <Link
                          to={movementPath(
                            reconciled.transfer.accountCode,
                            reconciled.transfer.movementId,
                          )}
                        >
                          {reconciled.transfer.accountAlias ?? reconciled.transfer.accountCode} del{' '}
                          {formatDate(reconciled.transfer.bookingDate!)}
                        </Link>
                        {reconciled.transfer.created && ' (movimento creato in Cassa)'}: si
                        annullano insieme.
                      </>
                    ) : (
                      <span className="error">
                        Da abbinare
                        {reconciled.transfer?.accountAlias
                          ? `: manca il movimento di ${reconciled.transfer.accountAlias}`
                          : ': l’altro conto non è indicato'}
                        . Annulla e rifai, o usa «Abbina da soli» in{' '}
                        <Link to="/tesoreria/giroconti">Giroconti</Link>.
                      </span>
                    )}
                  </small>
                )}
              </>
            )}
            {reconciled.kind === 'rateazione' && (
              <>
                Rate di rateazioni:
                <ul className="plain-list">
                  {(reconciled.instalments ?? []).map((item) => (
                    <li key={item.dueId}>
                      <Link to={`/tesoreria/rateazioni/${item.planId}`}>
                        Rata {item.number} · {item.planName}
                      </Link>{' '}
                      del {formatDate(item.dueDate)} — {formatMoney(item.amount)}
                    </li>
                  ))}
                  {reconciled.rest && (
                    <li>
                      Altro: «{reconciled.rest.note}» — {formatMoney(reconciled.rest.amount)}
                    </li>
                  )}
                </ul>
              </>
            )}
            {reconciled.kind === 'carte' && reconciled.cards && (
              <>
                Addebito delle carte di credito: spese di{' '}
                {canCards ? (
                  <Link to="/tesoreria/carte">{cardMonthLabel(reconciled.cards.month)}</Link>
                ) : (
                  cardMonthLabel(reconciled.cards.month)
                )}
                , riconosciuto dalla causale.
              </>
            )}
            {reconciled.kind === 'anticipo' && reconciled.advance && (
              <>
                {ADVANCE_MOVEMENT_LABELS[reconciled.advance.kind]}, riconosciuto dalla descrizione
                della banca: è un prestito sulle fatture, non un incasso.{' '}
                <small className="block">
                  {reconciled.advance.invoices.length > 0 ? (
                    <>
                      Fatture:{' '}
                      {reconciled.advance.invoices.map((item, index) => (
                        <span key={item.id}>
                          {index > 0 && ', '}
                          <Link to={`/tesoreria/fatture/${item.id}`}>{item.number}</Link>
                        </span>
                      ))}
                      .{' '}
                    </>
                  ) : reconciled.advance.kind === 'erogazione' ? (
                    'Le fatture presentate si indicano in '
                  ) : (
                    `La banca cita la fattura ${reconciled.advance.invoiceNumber}: si abbina quando l’anticipo ha le sue fatture, in `
                  )}
                  <Link to="/tesoreria/anticipi">Anticipi fatture</Link>.
                </small>
              </>
            )}
            {reconciled.kind === 'mutuo' && reconciled.loan && (
              <>
                {LOAN_MOVEMENT_KIND_LABELS[reconciled.loan.kind]} di{' '}
                <Link to={`/tesoreria/mutui/${reconciled.loan.id}`}>{reconciled.loan.name}</Link>
                {reconciled.auto && ', riconosciuta dal numero nella descrizione'}
                <small className="block">
                  capitale {formatMoney(reconciled.loan.capital)} · interessi{' '}
                  {formatMoney(reconciled.loan.interest)} · spese e altro{' '}
                  {formatMoney(reconciled.loan.fees)}
                </small>
                {reconciled.loan.reversedOn && (
                  <small className="warn block">
                    Stornata dalla banca il {formatDate(reconciled.loan.reversedOn)}: non conta per
                    il mutuo.
                  </small>
                )}
                {reconciled.note && <small className="block">{reconciled.note}</small>}
              </>
            )}
          </div>
          {undo.error && <p className="error">{undo.error.message}</p>}
          {save.error && <p className="error">{save.error.message}</p>}
          {rest.error && !resting && <p className="error">{rest.error.message}</p>}
          {removeGiven.error && <p className="error">{removeGiven.error.message}</p>}
          {canWrite && (
            <div className="actions">
              {freeCents > 0 && (
                <button type="button" className="primary" onClick={() => setAdding(true)}>
                  Aggiungi fatture per {formatMoney(fromCents(freeCents))}
                </button>
              )}
              {freeCents > 0 && (
                <button type="button" onClick={openRest}>
                  Il resto non è una fattura…
                </button>
              )}
              {freeCents > 0 && (
                <button type="button" onClick={() => setGiving(true)}>
                  Al soggetto, senza documento…
                </button>
              )}
              {!reconciled.auto && (
                <button type="button" onClick={() => undo.mutate()} disabled={undo.isPending}>
                  {undo.isPending ? 'Attendere…' : 'Annulla la riconciliazione'}
                </button>
              )}
              {reconciled.auto && reconciled.kind === 'spese' && (
                <button type="button" onClick={() => setNotCosts(true)}>
                  Non sono spese bancarie
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {adding && (
            <div className="notice small row between">
              <span>
                Altre fatture per i <strong>{formatMoney(fromCents(freeCents))}</strong> del
                movimento non ancora assegnati: quelle già collegate restano.
              </span>
              {canWrite && (
                <button type="button" className="small" onClick={openRest}>
                  Il resto non è una fattura…
                </button>
              )}
            </div>
          )}
          {!adding && advanceReading?.kind === 'saldo' && (
            <div className="notice small stack">
              <p>
                Il cliente ha pagato alla banca: sul conto arriva solo il resto. La banca ha
                trattenuto <strong>{formatMoney(advanceReading.retained)}</strong> per l’anticipo
                {advanceReading.invoiceNumber && <> sulla fattura {advanceReading.invoiceNumber}</>}
                .
              </p>
              {saldo.data ? (
                <>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <SortHeader sort={saldoSorting} column="invoice">
                          Fattura
                        </SortHeader>
                        <SortHeader sort={saldoSorting} column="fromMovement" numeric>
                          Dal conto
                        </SortHeader>
                        <SortHeader sort={saldoSorting} column="retained" numeric>
                          Trattenuto dalla banca
                        </SortHeader>
                        <SortHeader sort={saldoSorting} column="residual" numeric>
                          Da incassare
                        </SortHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {saldoSorting.rows.map((item) => (
                        <tr key={item.invoiceId}>
                          <td>
                            {item.number} del {formatDate(item.date)} · {item.counterpartyName}
                          </td>
                          <td className="numeric">{formatMoney(item.fromMovement)}</td>
                          <td className="numeric">{formatMoney(item.retained)}</td>
                          <td className="numeric">{formatMoney(item.residual)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {canWrite && (
                    <div className="actions">
                      <button
                        type="button"
                        className="primary"
                        disabled={save.isPending}
                        onClick={() =>
                          save.mutate({
                            kind: 'fatture',
                            allocations: saldo
                              .data!.invoices.filter((item) => toCents(item.fromMovement) > 0)
                              .map((item) => ({
                                invoiceId: item.invoiceId,
                                amount: item.fromMovement,
                              })),
                            retained: saldo.data!.invoices.map((item) => ({
                              invoiceId: item.invoiceId,
                              amount: item.retained,
                              advanceMovementId: item.advanceMovementId,
                            })),
                          })
                        }
                      >
                        Incassa le fatture per intero
                      </button>
                    </div>
                  )}
                </>
              ) : (
                saldo.isFetched && (
                  <p className="muted">
                    Nessuna fattura trovata: indica prima le fatture dell’anticipo in Anticipi
                    fatture, oppure riconcilia qui sotto il resto e segna il trattenuto dalla pagina
                    dell’anticipo.
                  </p>
                )
              )}
            </div>
          )}
          {!adding && suggested && reason === null && (
            <p className="notice small">
              La descrizione parla di {RECONCILIATION_REASON_LABELS[suggested].toLowerCase()}:{' '}
              {canWrite ? (
                <button type="button" className="link" onClick={() => setReason(suggested)}>
                  riconcilia come {RECONCILIATION_REASON_LABELS[suggested]}
                </button>
              ) : (
                'non è una fattura.'
              )}
            </p>
          )}
          {/* «Non è una fattura» leaves only the reason on screen (1.45). */}
          {reason === null && (
            <>
              <h3>{incoming ? 'Fatture emesse da incassare' : 'Fatture ricevute da pagare'}</h3>
              <div className="reconcile-filters">
                <label>
                  Fatture dal
                  <input
                    type="date"
                    value={from}
                    onChange={(event) => setFrom(event.target.value)}
                  />
                </label>
                <label>
                  al
                  <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
                </label>
                {recognised && (
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={onlyCounterparty}
                      onChange={(event) => setOnlyCounterparty(event.target.checked)}
                    />
                    Solo {recognised.counterpartyName}
                  </label>
                )}
              </div>
              {proposals.isPending && <p className="muted">Ricerca delle fatture…</p>}
              {proposals.data && candidates.length === 0 && (
                <p className="muted small">
                  Nessuna fattura proposta dalle regole: cercala qui sotto, o indica che non è una
                  fattura.
                </p>
              )}
              {shown.length < candidates.length && (
                <p className="muted small">
                  {candidates.length - shown.length === 1
                    ? 'Una proposta è fuori dai filtri scelti.'
                    : `${candidates.length - shown.length} proposte sono fuori dai filtri scelti.`}
                </p>
              )}
              <ul className="reconcile-list">
                {shown.map((candidate) => {
                  const selected = chosen[candidate.invoiceId] !== undefined;
                  return (
                    <li key={candidate.invoiceId} className={candidate.best ? 'best' : undefined}>
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={!canWrite}
                          onChange={() => toggle(candidate)}
                        />
                        <span>
                          <CreditNoteMark kind={candidate.kind} />
                          <strong>
                            {candidate.number} · {candidate.counterpartyName}
                          </strong>
                          {candidate.origin === 'apertura' && (
                            <span className="pill info">situazione iniziale</span>
                          )}
                          {candidate.best && <span className="pill ok">consigliata</span>}
                          <small className="muted block">
                            del {formatDate(candidate.date)} · resta{' '}
                            {formatMoney(candidate.residual)}
                            {candidate.date > movement.bookingDate &&
                              ' · successiva al movimento (pagata su proforma?)'}
                            {candidate.reasons.length > 0 &&
                              ` · ✓ ${candidate.reasons.join(' · ✓ ')}`}
                          </small>
                        </span>
                      </label>
                      {selected && (
                        <DecimalInput
                          aria-label={`Importo per ${candidate.number}`}
                          value={chosen[candidate.invoiceId]!}
                          onChange={(value) =>
                            setChosen({ ...chosen, [candidate.invoiceId]: value })
                          }
                          decimals={[2, 2]}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
              {canWrite && (
                <>
                  <h3>Altre fatture</h3>
                  <label className="search-box">
                    <Icon name="search" />
                    <input
                      value={search}
                      placeholder="Cerca: numero, controparte, importo"
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </label>
                  {invoices.isPending && <p className="muted small">Caricamento delle fatture…</p>}
                  {invoices.data && others.length === 0 && (
                    <p className="muted small">Nessuna altra fattura aperta con questi filtri.</p>
                  )}
                  {others.length > 0 && (
                    <ul className="search-results">
                      {others.slice(0, LIST_LIMIT).map((item) => (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => {
                              const candidate = {
                                invoiceId: item.id,
                                number: item.number,
                                date: item.date,
                                subjectId: item.subjectId,
                                counterpartyName: item.counterpartyName,
                                residual: item.residual,
                                reasons: [],
                                origin: item.origin,
                                kind: item.kind,
                                best: false,
                              };
                              if (!candidates.some((other) => other.invoiceId === item.id))
                                setCandidates([...candidates, candidate]);
                              toggle(candidate);
                            }}
                          >
                            <CreditNoteMark kind={item.kind} />
                            <strong>
                              {item.number} · {item.counterpartyName}
                            </strong>
                            {item.origin === 'apertura' && (
                              <span className="pill info">situazione iniziale</span>
                            )}
                            <small className="muted block">
                              del {formatDate(item.date)} · resta {formatMoney(item.residual)}
                            </small>
                          </button>
                        </li>
                      ))}
                      {others.length > LIST_LIMIT && (
                        <li className="muted small">
                          Altre {others.length - LIST_LIMIT}: restringi le date o cerca.
                        </li>
                      )}
                    </ul>
                  )}
                </>
              )}
            </>
          )}
          {reason !== null && (
            <div className="card stack">
              <Field label="Non è una fattura: perché">
                <select
                  value={reason}
                  onChange={(event) => setReason(event.target.value as ReconciliationReason)}
                >
                  {RECONCILIATION_REASONS.filter((item) => item !== 'storno_rata' || incoming).map(
                    (item) => (
                      <option key={item} value={item}>
                        {RECONCILIATION_REASON_LABELS[item]}
                      </option>
                    ),
                  )}
                </select>
              </Field>
              {reason === 'stipendio' && canPeople && !incoming && (
                <SalaryLines
                  code={code}
                  movement={movement}
                  lines={salaryLines}
                  onChange={setSalaryLines}
                />
              )}
              {reason === 'tfr' && canPeople && !incoming && (
                <TfrLines movement={movement} lines={tfrLines} onChange={setTfrLines} />
              )}
              {reason === 'finanziamento' && (
                <Field
                  label="Mutuo o finanziamento"
                  hint="Per un movimento senza il numero del mutuo, come la restituzione di un prestito socio: tutto capitale."
                >
                  <select value={loanId} onChange={(event) => setLoanId(event.target.value)}>
                    <option value="">— nessuno, solo il motivo —</option>
                    {(loans.data ?? [])
                      .filter((loan) => loan.active)
                      .map((loan) => (
                        <option key={loan.id} value={loan.id}>
                          {loan.name}
                        </option>
                      ))}
                  </select>
                </Field>
              )}
              {isTransferReason(reason) && (
                <TransferPicker movement={movement} reason={reason} onChange={setTransfer} />
              )}
              {reason === 'storno_rata' && (
                <ReversalPicker code={code} movement={movement} onChange={setReversedId} />
              )}
              {reason === 'rateazione' && (
                <>
                  <InstalmentDuesPicker movement={movement} onChange={onDues} />
                  {dues.cents > 0 && dues.cents < movementCents && (
                    <Field
                      label={`Il resto, ${formatMoney(fromCents(movementCents - dues.cents))}: che cos’è`}
                      hint="Es. gli altri tributi dello stesso F24."
                    >
                      <input
                        value={duesRest}
                        maxLength={300}
                        onChange={(event) => setDuesRest(event.target.value)}
                      />
                    </Field>
                  )}
                  {dues.cents > movementCents && (
                    <p className="error small">Le rate scelte superano il movimento.</p>
                  )}
                </>
              )}
              <Field label="Nota">
                <input
                  value={note}
                  maxLength={300}
                  onChange={(event) => setNote(event.target.value)}
                />
              </Field>
            </div>
          )}

          <div className="reconcile-footer">
            {reason === null && picked.length > 0 && (
              <div className="reconcile-sum">
                {/* Per invoice: what was left, what this movement pays, what stays open (1.45). */}
                <table className="per-invoice">
                  <thead>
                    <tr>
                      <th>Fattura</th>
                      <th>Residuo</th>
                      <th>Paga ora</th>
                      <th>Resta aperta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {picked.map((candidate) => {
                      const residual = Math.abs(toCents(candidate.residual));
                      const paid = centsOf(chosen[candidate.invoiceId]!);
                      return (
                        <tr key={candidate.invoiceId}>
                          <td>{candidate.number}</td>
                          <td>{formatMoney(fromCents(residual))}</td>
                          <td>{formatMoney(fromCents(paid))}</td>
                          <td className={paid >= residual ? 'ok' : undefined}>
                            {paid >= residual
                              ? 'saldata ✓'
                              : formatMoney(fromCents(residual - paid))}
                          </td>
                        </tr>
                      );
                    })}
                    {picked.length > 1 && (
                      <tr className="total">
                        <td>Totale</td>
                        <td>{formatMoney(fromCents(pickedResidual))}</td>
                        <td>{formatMoney(fromCents(allocated))}</td>
                        <td>{formatMoney(fromCents(Math.max(0, pickedResidual - allocated)))}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {allocated === movementCents ? (
                  <small className="muted block">
                    Movimento {formatMoney(fromCents(movementCents))} · tutto assegnato ✓
                  </small>
                ) : (
                  <p className="warn small">
                    {allocated < movementCents
                      ? `Mancano ${formatMoney(fromCents(movementCents - allocated))} da assegnare`
                      : `Gli importi superano il movimento di ${formatMoney(fromCents(allocated - movementCents))}`}{' '}
                    (movimento {formatMoney(fromCents(movementCents))}, assegnati{' '}
                    {formatMoney(fromCents(allocated))}).
                  </p>
                )}
                {overpaid.map((candidate) => (
                  <p key={candidate.invoiceId} className="warn small">
                    Alla {candidate.number} assegni più del residuo.
                  </p>
                ))}
                {smallLeft.map((candidate) => (
                  <label key={candidate.invoiceId} className="checkbox small block">
                    <input
                      type="checkbox"
                      checked={!keepOpen.includes(candidate.invoiceId)}
                      onChange={(event) =>
                        setKeepOpen(
                          event.target.checked
                            ? keepOpen.filter((id) => id !== candidate.invoiceId)
                            : [...keepOpen, candidate.invoiceId],
                        )
                      }
                    />{' '}
                    Chiudi con un abbuono la differenza di{' '}
                    {formatMoney(fromCents(leftOf(candidate)))} sulla {candidate.number}
                  </label>
                ))}
              </div>
            )}
            {save.error && <p className="error">{save.error.message}</p>}
            {canWrite && (
              <div className="actions">
                {adding && (
                  <button type="button" onClick={() => setAdding(false)} disabled={save.isPending}>
                    ← Torna alla riconciliazione
                  </button>
                )}
                {notCosts && (
                  <button
                    type="button"
                    onClick={() => setNotCosts(false)}
                    disabled={save.isPending}
                  >
                    ← Sono spese bancarie
                  </button>
                )}
                {!adding && !incoming && reason === null && (
                  <button
                    type="button"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ kind: 'spese' })}
                  >
                    Spese bancarie
                  </button>
                )}
                {!adding && reason === null && (
                  <button type="button" onClick={() => setGiving(true)} disabled={save.isPending}>
                    Al soggetto, senza documento…
                  </button>
                )}
                {adding ? null : reason === null ? (
                  <button
                    type="button"
                    onClick={() => setReason('altro')}
                    disabled={save.isPending}
                  >
                    Non è una fattura…
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={() => setReason(null)} disabled={save.isPending}>
                      ← Torna alle fatture
                    </button>
                    {salaryLinked ? (
                      <button
                        type="button"
                        className="primary"
                        disabled={
                          save.isPending ||
                          salaryCents === null ||
                          salaryCents === 0 ||
                          salaryCents > movementCents
                        }
                        onClick={() =>
                          confirmShort(
                            {
                              kind: 'stipendi',
                              lines: salaryLines.map((line) => ({
                                employeeId: line.employeeId,
                                year: line.year,
                                month: line.month,
                                kind: line.kind,
                                amount: moneyOf(line.amount) ?? '',
                              })),
                            },
                            salaryCents ?? 0,
                          )
                        }
                      >
                        Conferma lo stipendio
                      </button>
                    ) : tfrLinked ? (
                      <button
                        type="button"
                        className="primary"
                        disabled={
                          save.isPending ||
                          tfrCents === null ||
                          tfrCents === 0 ||
                          tfrCents > movementCents
                        }
                        onClick={() =>
                          confirmShort(
                            {
                              kind: 'tfr',
                              lines: tfrLines.map((line) => ({
                                employeeId: line.employeeId,
                                amount: moneyOf(line.amount) ?? '',
                              })),
                              note,
                            },
                            tfrCents ?? 0,
                          )
                        }
                      >
                        Conferma il TFR
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="primary"
                        disabled={
                          save.isPending ||
                          (isTransferReason(reason) && !transfer) ||
                          (reason === 'storno_rata' && !reversedId) ||
                          (reason === 'rateazione' &&
                            (dues.ids.length === 0 ||
                              dues.cents > movementCents ||
                              (dues.cents < movementCents && !duesRest.trim())))
                        }
                        onClick={() =>
                          save.mutate(
                            reason === 'rateazione'
                              ? {
                                  kind: 'rateazione',
                                  dueIds: dues.ids,
                                  note,
                                  ...(dues.cents < movementCents
                                    ? {
                                        rest: {
                                          amount: fromCents(movementCents - dues.cents),
                                          note: duesRest.trim(),
                                        },
                                      }
                                    : {}),
                                }
                              : reason === 'finanziamento' && loanId
                                ? { kind: 'mutuo', loanId, note }
                                : isTransferReason(reason) && transfer
                                  ? { kind: 'motivo', reason, note, transfer }
                                  : reason === 'storno_rata' && reversedId
                                    ? {
                                        kind: 'motivo',
                                        reason,
                                        note,
                                        reversedMovementId: reversedId,
                                      }
                                    : { kind: 'motivo', reason, note },
                          )
                        }
                      >
                        Conferma il motivo
                      </button>
                    )}
                  </>
                )}
                {reason === null && (
                  <button
                    type="button"
                    className="primary"
                    disabled={save.isPending || allocated === 0 || allocated > movementCents}
                    onClick={() =>
                      confirmShort(
                        {
                          kind: 'fatture',
                          allocations: Object.entries(chosen).map(([invoiceId, value]) => ({
                            invoiceId,
                            amount: fromCents(centsOf(value)),
                          })),
                          writeOffs,
                        },
                        allocated,
                      )
                    }
                  >
                    {save.isPending
                      ? 'Attendere…'
                      : Object.keys(chosen).length > 1
                        ? `Riconcilia con ${Object.keys(chosen).length} fatture`
                        : 'Riconcilia'}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {short && (
        <ConfirmDialog
          title="Resta una parte non assegnata"
          confirmLabel="Conferma comunque"
          busy={save.isPending}
          error={save.error?.message}
          onConfirm={() =>
            save.mutate(
              short.body.kind === 'fatture' && !adding && shortNote.trim()
                ? {
                    ...short.body,
                    rest: { amount: fromCents(short.left), note: shortNote.trim() },
                  }
                : short.body,
              {
                onSettled: () => {
                  setShort(null);
                  setShortNote('');
                },
              },
            )
          }
          onCancel={() => {
            setShort(null);
            setShortNote('');
          }}
        >
          <p>
            Restano <strong>{formatMoney(fromCents(short.left))}</strong> del movimento non
            assegnati a {short.body.kind === 'fatture' ? 'nessuna fattura' : 'nessun dipendente'}.
            Confermi lo stesso?
          </p>
          {short.body.kind === 'fatture' && !adding && (
            <Field label="Se il resto non è una fattura, scrivi cos’è">
              <input
                value={shortNote}
                maxLength={300}
                placeholder="Es. liquidazione recesso quote socio"
                onChange={(event) => setShortNote(event.target.value)}
              />
            </Field>
          )}
          {short.body.kind === 'fatture' && (
            <p className="muted small">
              {shortNote.trim()
                ? 'Il movimento risulterà tutto assegnato: le fatture e il resto con la tua descrizione.'
                : 'Il movimento resterà «da completare»: le fatture mancanti si aggiungono dopo, dal movimento o dalla fattura con «Cerca in banca».'}
            </p>
          )}
        </ConfirmDialog>
      )}
      {giving && (
        <SubjectPaymentDialog
          base={base}
          reconciled={Boolean(movement.reconciliation)}
          freeCents={movement.reconciliation ? freeCents : Math.abs(toCents(movement.amount))}
          incoming={incoming}
          proposed={
            recognised?.subjectId
              ? { id: recognised.subjectId, name: recognised.counterpartyName }
              : null
          }
          onClose={() => setGiving(false)}
          onDone={() => {
            setGiving(false);
            setAdding(false);
            done();
          }}
        />
      )}
      {resting && (
        <ConfirmDialog
          title="Il resto del movimento non è una fattura"
          confirmLabel="Assegna il resto"
          busy={rest.isPending}
          disabled={!resting.note.trim() || moneyOf(resting.amount) === null}
          error={rest.error?.message}
          onConfirm={() =>
            rest.mutate({ amount: moneyOf(resting.amount) ?? '', note: resting.note.trim() })
          }
          onCancel={() => setResting(null)}
        >
          <Field label="Cos’è">
            <input
              autoFocus
              value={resting.note}
              maxLength={300}
              placeholder="Es. liquidazione recesso quote socio"
              onChange={(event) => setResting({ ...resting, note: event.target.value })}
            />
          </Field>
          <Field label="Importo">
            <DecimalInput
              value={resting.amount}
              onChange={(value) => setResting({ ...resting, amount: value })}
              decimals={[2, 2]}
            />
          </Field>
          <p className="muted small">
            Restano {formatMoney(fromCents(freeCents))} non assegnati a fatture. Con il resto
            assegnato il movimento risulta completo; si può togliere dal movimento.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
